import VideoCard from "./video-card"
import type { Video, VideoDetail } from "@/lib/api"
import type { ReactNode } from "react"
import { cloneElement, isValidElement } from "react"
import { cn } from "@/lib/utils"

interface VideoGridProps {
  videos: (Video | VideoDetail)[]
  onVideoClick?: (video: Video | VideoDetail) => void
  insertCardEvery?: number
  insertedCard?: ReactNode
  insertedCards?: ReactNode[]
  /** When set, called for each insertion (index 0 = first slot). Overrides insertedCard / insertedCards. */
  renderInsertedSlot?: (insertionIndex: number) => ReactNode
  /** Insert a full-width inline ad row after every N videos (e.g. 4 on mobile). */
  insertAdEveryNVideos?: number
  renderInlineAdSlot?: (adIndex: number) => ReactNode
  /** @deprecated Use insertAdEveryNVideos — single ad after one index only */
  insertAfterVideoIndex?: number
  insertAfterVideoSlot?: ReactNode
  className?: string
}

const VideoGrid = ({
  videos,
  onVideoClick,
  insertCardEvery,
  insertedCard,
  insertedCards,
  renderInsertedSlot,
  insertAdEveryNVideos,
  renderInlineAdSlot,
  insertAfterVideoIndex,
  insertAfterVideoSlot,
  className,
}: VideoGridProps) => {
  const cards: ReactNode[] = []
  let insertionIndex = 0
  let inlineAdIndex = 0

  videos.forEach((video, index) => {
    cards.push(
      <VideoCard
        key={`${video.id}-${index}`}
        video={video}
        onVideoClick={onVideoClick}
        priority={index === 0}
        // First row can sit in-viewport on tall phones; don't lazy those.
        eager={index > 0 && index < 4}
      />
    )

    const shouldInsert =
      insertCardEvery &&
      insertCardEvery > 0 &&
      (index + 1) % insertCardEvery === 0 &&
      (renderInsertedSlot ||
        insertedCard ||
        (insertedCards && insertedCards.length > 0))

    if (shouldInsert) {
      const rawSlot = renderInsertedSlot
        ? renderInsertedSlot(insertionIndex)
        : insertedCards && insertedCards.length > 0
          ? insertedCards[insertionIndex % insertedCards.length]
          : insertedCard

      if (rawSlot == null) {
        insertionIndex += 1
      } else {
        const renderedCard = isValidElement(rawSlot)
          ? cloneElement(rawSlot, { key: `inserted-card-content-${index}` })
          : rawSlot

        cards.push(
          <div key={`inserted-card-${index}`} className="col-span-full w-full min-w-0">
            {renderedCard}
          </div>
        )
        insertionIndex += 1
      }
    }

    if (
      insertAdEveryNVideos &&
      insertAdEveryNVideos > 0 &&
      renderInlineAdSlot &&
      (index + 1) % insertAdEveryNVideos === 0
    ) {
      const adIndex = inlineAdIndex++
      cards.push(
        <div
          key={`inline-ad-${adIndex}-after-${index}`}
          className="col-span-full my-1 flex w-full min-w-0 justify-center sm:my-2"
        >
          {renderInlineAdSlot(adIndex)}
        </div>
      )
    } else if (
      insertAfterVideoSlot != null &&
      insertAfterVideoIndex !== undefined &&
      index === insertAfterVideoIndex
    ) {
      cards.push(
        <div
          key={`insert-after-video-${insertAfterVideoIndex}`}
          className="col-span-full my-1 w-full min-w-0 sm:my-2"
        >
          {insertAfterVideoSlot}
        </div>
      )
    }
  })

  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-6 mt-6",
        className
      )}
    >
      {cards}
    </div>
  )
}

export default VideoGrid
